import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { H1, P } from '../../common/Typography';
import { Spacer } from '../../common/Spacer';
import { useAccountState } from '../../providers/AccountStateProvider';
import { styles } from '../../../styles';
import { Button } from '../../common/Button';
import { SavedConfigurationsTable } from './SavedConfigurationsTable';
import { ApplicationTypes } from '../../../store/application/store';
import { useQueryParams as useFlatRoofQueryParams } from '../../../store/query/flat-roof.hooks';
import { useQueryParams as useCavityWallQueryParams } from '../../../store/query/cavity-wall.hooks';
import { useQueryParams as useSlopedRoofQueryParams } from '../../../store/query/sloped-roof.hooks';
import { useQueryParams as useVentilatedWallQueryParams } from '../../../store/query/ventilated-wall.hooks';
import { useQueryParams as useFloorQueryParams } from '../../../store/query/floor.hooks';
import { useQueryParams as useSolQueryParams } from '../../../store/query/sol.hooks';
import { useQueryParams as useToitEnPenteQueryParams } from '../../../store/query/toit-en-pente.hooks';
import { useModal } from '../../../store/modal/hooks';

export const PageSection = styled.section`
  z-index: 8;
  position: absolute;
  inset: 0;
  width: 100%;
  min-width: 340px;
  height: 100%;
  background: ${styles.colors.white};
  border-top: 1px solid ${styles.colors.darkBlue5};
  padding-bottom: ${(props) => props.theme.designWidth(96)}px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${(props) => props.theme.designWidth(24)}px;

  @media screen and (pointer: coarse) {
    min-width: 100%;
  }
`;

const WelcomeSection = styled.div`
  background: #f8f9fa;
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;

  @media screen and (pointer: coarse) {
    flex-direction: column;
  }
`;

const WelcomeStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
`;

// const UserInfo = styled.div`
//   display: grid;
//   grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
//   gap: 16px;
//   margin-top: 24px;
// `;

// const InfoCard = styled.div`
//   background: white;
//   padding: 16px;
//   border-radius: 8px;
//   border: 1px solid #e9ecef;
// `;

// const InfoLabel = styled.div`
//   font-size: 12px;
//   color: #6c757d;
//   text-transform: uppercase;
//   letter-spacing: 0.5px;
//   margin-bottom: 4px;
// `;

// const InfoValue = styled.div`
//   font-size: 16px;
//   font-weight: 500;
//   color: #212529;
// `;

export const Dashboard = () => {
  const { t } = useTranslation() as any;
  const account = useAccountState();
  const { setQueryParams: setFlatRoofQueryParams } = useFlatRoofQueryParams();
  const { setQueryParams: setCavityWallQueryParams } = useCavityWallQueryParams();
  const { setQueryParams: setSlopedRoofQueryParams } = useSlopedRoofQueryParams();
  const { setQueryParams: setVentilatedWallQueryParams } = useVentilatedWallQueryParams();
  const { setQueryParams: setFloorQueryParams } = useFloorQueryParams();
  const { setQueryParams: setSolQueryParams } = useSolQueryParams();
  const { setQueryParams: setToitEnPenteQueryParams } = useToitEnPenteQueryParams();
  const { displaySaveConfigurationModal, displaySaveConfigurationSuccessModal } = useModal();

  useEffect(() => {
    // Ensure account data is loaded when Dashboard mounts
    if (!account.isLoading && !account.user && !account.error) {
      account.loadAccountData();
    }
  }, [account]);

  if (account.isLoading) {
    return (
      <PageSection>
        <P>{t('account.loading')}...</P>
      </PageSection>
    );
  }

  if (account.error || !account.isAuthenticated || !account.user) {
    return (
      <PageSection>
        <P>{t('account.error.label')}: {account.error || t('account.error.userNotAuthenticated')}</P>
      </PageSection>
    );
  }

  const handleManageClick = () => {
    if (process.env.REACT_APP_MY_IKO_ACCOUNT_URL) {
      window.open(process.env.REACT_APP_MY_IKO_ACCOUNT_URL, '_blank')
    }
  };

  const handleLogoutClick = () => {
    account.logout();
  };

  const handleEditConfiguration = (configuration: any) => {
    displaySaveConfigurationModal(async (name: string, projectId?: string) => {
      const updatedConfiguration = {
        ...configuration,
        display_name: name,
        project_id: projectId ?? ""
      };
      account.editConfiguration(updatedConfiguration).then(() => {
        displaySaveConfigurationSuccessModal();
      })
    }, configuration.display_name, true, configuration.project_id);
  };

  const handleDeleteConfiguration = (configuration: any) => {
    account.deleteConfiguration(configuration);
  };

  const handleLoadConfiguration = (configuration: any) => {
    const data = JSON.parse(configuration.application_data)
    const lang = configuration.language
    switch (configuration.application_type) {
      case ApplicationTypes.FLAT_ROOF:
        setFlatRoofQueryParams(data, lang, true);
        break;
      case ApplicationTypes.CAVITY_WALL:
        setCavityWallQueryParams(data, lang, true);
        break
      case ApplicationTypes.SLOPED_ROOF:
        setSlopedRoofQueryParams(data, lang, true);
        break
      case ApplicationTypes.OUTER_WALL:
        setVentilatedWallQueryParams(data, lang, true);
        break
      case ApplicationTypes.FLOOR:
        setFloorQueryParams(data, lang, true);
        break
      case ApplicationTypes.SOL:
        setSolQueryParams(data, lang, true);
        break
      case ApplicationTypes.TOIT_EN_PENTE:
        setToitEnPenteQueryParams(data, lang, true);
        break
      default:
        alert('Unsupported application type');
        break;
    }
  };

  return (
    <PageSection>
      <WelcomeSection>
        <WelcomeStack>
          <H1>{t('account.dashboard.title')}</H1>
          <Spacer heightPx={8} />
          <P>
            {t('account.dashboard.greeting')} {account.displayName}!
            <br />
            {t('account.dashboard.subtitle')}
          </P>
        </WelcomeStack>
        <WelcomeStack>
          <Button onClick={handleManageClick}>{t('account.manage')}</Button>
          <Button onClick={handleLogoutClick}>{t('account.logout')}</Button>
        </WelcomeStack>
      </WelcomeSection>

      {/* <UserInfo>
        <InfoCard>
          <InfoLabel>Display Name</InfoLabel>
          <InfoValue>{account.displayName || '/'}</InfoValue>
        </InfoCard>

        <InfoCard>
          <InfoLabel>Role</InfoLabel>
          <InfoValue>{account.userRole || '/'}</InfoValue>
        </InfoCard>

        <InfoCard>
          <InfoLabel>Language</InfoLabel>
          <InfoValue>{account.userLanguage || '/'}</InfoValue>
        </InfoCard>

        <InfoCard>
          <InfoLabel>User ID</InfoLabel>
          <InfoValue>{account.userId || '/'}</InfoValue>
        </InfoCard>
      </UserInfo> */}

      <Spacer heightPx={16} />

      <SavedConfigurationsTable
        isAdminView={account.isAdmin}
        configurations={[...account.configurations].sort((a, b) => a.created_at > b.created_at ? -1 : 1) || []}
        onEdit={handleEditConfiguration}
        onDelete={handleDeleteConfiguration}
        onLoad={handleLoadConfiguration}
      />
    </PageSection>
  );
};